import { useEffect, useState, type ReactNode } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { WorkspaceTopBar, type WorkspaceSessionTab, type CanvasActions, type SaveState } from '@genieartinc/studio-ui'
import { useSession } from '@/lib/auth-client'

/** Open sessions are navigation preferences; closing a tab never deletes a canvas. */
export function StudioSessionTabs({ canvasId, title, actions, saveState, busy, panelControls }: {
  canvasId: string
  title: string
  actions: CanvasActions
  saveState: SaveState
  busy: boolean
  panelControls?: ReactNode
}) {
  const navigate = useNavigate()
  const session = useSession()
  const storageKey = session.data?.user.id ? `genie.web.sessions.${session.data.user.id}` : null
  const [tabs, setTabs] = useState<WorkspaceSessionTab[]>([])
  useEffect(() => {
    let saved: WorkspaceSessionTab[] = []
    try {
      const value: unknown = storageKey ? JSON.parse(localStorage.getItem(storageKey) ?? '[]') : []
      if (Array.isArray(value)) saved = value.filter((tab): tab is WorkspaceSessionTab => !!tab && typeof tab.id === 'string' && typeof tab.title === 'string')
    } catch { /* A blocked or unavailable storage area does not prevent navigation. */ }
    const next = saved.some(tab => tab.id === canvasId)
      ? saved.map(tab => tab.id === canvasId ? { ...tab, title } : tab)
      : [...saved, { id: canvasId, title }]
    setTabs(next)
    try { if (storageKey) localStorage.setItem(storageKey, JSON.stringify(next)) } catch { /* Keep tabs for this page. */ }
  }, [storageKey, canvasId, title])

  const close = (id: string) => {
    const next = tabs.filter(tab => tab.id !== id)
    setTabs(next)
    try { if (storageKey) localStorage.setItem(storageKey, JSON.stringify(next)) } catch { /* Keep tabs for this page. */ }
    if (id === canvasId) {
      const adjacent = next[Math.min(tabs.findIndex(tab => tab.id === id), next.length - 1)]
      if (adjacent) void navigate({ to: '/canvas/$id', params: { id: adjacent.id } })
      else void navigate({ to: '/workspace' })
    }
  }

  return <WorkspaceTopBar
    panelControls={panelControls}
    tabs={tabs.length ? tabs : [{ id: canvasId, title }]} activeId={canvasId} actions={actions} saveState={saveState} busy={busy}
    onSelect={id => { if (id !== canvasId) void navigate({ to: '/canvas/$id', params: { id } }) }}
    onClose={close} onNew={() => void navigate({ to: '/canvas/new' })} onHome={() => void navigate({ to: '/workspace' })}
    onRename={id => { if (id === canvasId) actions.onRename?.() }}
  />
}
